import { useMemo } from "react";
import type { z } from "zod";

import type { Prettify } from "@thalamos/common";
import type { WorkflowVersion } from "@thalamos/vision-common";
import {
  createInitialValues,
  FormSectionSchema,
  tk,
  workflows,
} from "@thalamos/vision-common";

import type { MultiPageFormProps } from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import {
  MultiPageForm,
  MultiPageFormSkeleton,
} from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import { Error } from "../../pages/Error/Error.js";

type RecordPolicePresenceSchema<T extends WorkflowVersion> = z.infer<
  ReturnType<
    (typeof workflows)[T]["requestPolicePresence"]["formDefinition"]["sections"][1]["buildFormSpecification"]
  >["formSchema"]
>;

///

export function RecordPolicePresenceFormSkeleton() {
  return (
    <MultiPageFormSkeleton headerText={tk("recordPolicePresence.title")} />
  );
}

///

export type RecordPolicePresenceFormProps<T extends WorkflowVersion> = Prettify<
  {
    formId?: string;
    workflowVersion: T;
  } & Pick<
    MultiPageFormProps<RecordPolicePresenceSchema<T>>,
    "initialValues" | "incidentWorkItem" | "onSubmit" | "onCancel"
  >
>;

///

export function RecordPolicePresenceForm<T extends WorkflowVersion>(
  props: RecordPolicePresenceFormProps<T>,
) {
  const workflow = workflows[props.workflowVersion];
  const section = workflow?.requestPolicePresence.formDefinition.sections.find(
    (section) => section.id === FormSectionSchema.enum.recordPolicePresence,
  );

  const formSpecification = section?.buildFormSpecification({
    incidentWorkItem: props.incidentWorkItem,
    formId: props.formId,
  });
  const formSchema = formSpecification?.formSchema;
  const strictFieldSchemas = formSpecification?.strictFieldSchemas;

  const initialValuesOrDefaults = useMemo(() => {
    if (!section || !strictFieldSchemas || !formSchema) {
      return {};
    }
    return createInitialValues(strictFieldSchemas, props.initialValues);
  }, [props, strictFieldSchemas, section, formSchema]);

  if (!section || !formSchema || !strictFieldSchemas) {
    return <Error />;
  }

  return (
    <MultiPageForm<typeof formSchema>
      headerText={tk("recordPolicePresence.title")}
      incidentWorkItem={props.incidentWorkItem}
      schema={formSchema}
      strictFieldSchemas={strictFieldSchemas}
      initialValues={initialValuesOrDefaults}
      pages={formSpecification.pages}
      onCancel={props.onCancel}
      onSubmit={props.onSubmit}
    />
  );
}
